import axios, { AxiosError } from "axios";
import { toast } from "sonner";

const baseURL = import.meta.env.VITE_API_BASE ?? "http://127.0.0.1:8765";

export const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

type AuthHandlers = {
  onUnauthorized: () => void;
};

let handlers: AuthHandlers = {
  onUnauthorized: () => {
    // default no-op until AuthProvider wires it up
  },
};

export function registerAuthHandlers(h: AuthHandlers) {
  handlers = h;
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error_code?: string; message?: string }>) => {
    const status = error.response?.status;
    if (status === 401) {
      handlers.onUnauthorized();
    } else if (status === 403) {
      const message =
        error.response?.data?.message ?? "Zugriff verweigert";
      toast.error(message);
    }
    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export type Me = {
  id: string;
  email: string;
  display_name: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
};

export async function fetchMe(): Promise<Me> {
  const { data } = await api.get<Me>("/auth/me");
  return data;
}

export async function login(email: string, password: string): Promise<void> {
  await api.post("/auth/login", { email, password });
}

export async function logout(): Promise<void> {
  await api.post("/auth/logout");
}

// ---------------------------------------------------------------------------
// /me/api-keys
// ---------------------------------------------------------------------------

export type ApiKeySummary = {
  id: string;
  label: string;
  prefix: string;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  owner_email?: string | null;
};

export type CreateApiKeyResponse = {
  id: string;
  label: string;
  prefix: string;
  /** Raw plaintext key — backend returns it exactly once. */
  key: string;
  created_at: string;
};

export async function listMyApiKeys(): Promise<ApiKeySummary[]> {
  const { data } = await api.get<ApiKeySummary[]>("/me/api-keys");
  return data;
}

export async function createMyApiKey(
  label: string,
): Promise<CreateApiKeyResponse> {
  const { data } = await api.post<CreateApiKeyResponse>("/me/api-keys", {
    label,
  });
  return data;
}

export async function revokeMyApiKey(keyId: string): Promise<void> {
  await api.delete(`/me/api-keys/${keyId}`);
}

// ---------------------------------------------------------------------------
// /me/usage
// ---------------------------------------------------------------------------

export type UsageBucket = {
  bucket: string;
  tool_calls: number;
  prompt_tokens: number;
  completion_tokens: number;
  search_credits: number;
  run_count: number;
};

export type UsageResponse = {
  granularity: "day" | "hour";
  from: string;
  to: string;
  buckets: UsageBucket[];
};

export type UsageQuery = {
  from?: string;
  to?: string;
  granularity?: "day" | "hour";
  api_key_id?: string;
};

export async function fetchMyUsage(q: UsageQuery): Promise<UsageResponse> {
  const { data } = await api.get<UsageResponse>("/me/usage", { params: q });
  return data;
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export type UserSummary = {
  id: string;
  email: string;
  display_name: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
};

export async function listUsers(filters?: {
  is_admin?: boolean;
  is_active?: boolean;
}): Promise<UserSummary[]> {
  const { data } = await api.get<UserSummary[]>("/admin/users", {
    params: filters,
  });
  return data;
}

export async function createUser(payload: {
  email: string;
  display_name: string;
  password: string;
  is_admin: boolean;
}): Promise<UserSummary> {
  const { data } = await api.post<UserSummary>("/admin/users", payload);
  return data;
}

export async function updateUser(
  userId: string,
  payload: Partial<{
    display_name: string;
    is_admin: boolean;
    is_active: boolean;
    password: string;
  }>,
): Promise<UserSummary> {
  const { data } = await api.patch<UserSummary>(
    `/admin/users/${userId}`,
    payload,
  );
  return data;
}

export async function revokeUserSessions(userId: string): Promise<void> {
  await api.delete(`/admin/users/${userId}/sessions`);
}

export async function adminListApiKeys(filters?: {
  user_id?: string;
  only_active?: boolean;
}): Promise<ApiKeySummary[]> {
  const { data } = await api.get<ApiKeySummary[]>("/admin/api-keys", {
    params: filters,
  });
  return data;
}

export async function adminFetchUsage(
  q: UsageQuery & { user_id?: string },
): Promise<UsageResponse> {
  const { data } = await api.get<UsageResponse>("/admin/usage", { params: q });
  return data;
}

// ---------------------------------------------------------------------------
// /admin/revenue
// ---------------------------------------------------------------------------

export type RevenueRow = {
  user_id: string;
  email: string;
  display_name: string;
  tool_calls: number;
  prompt_tokens: number;
  completion_tokens: number;
  run_count: number;
};

export type RevenueResponse = {
  from: string;
  to: string;
  rows: RevenueRow[];
};

export type RevenueQuery = {
  from?: string;
  to?: string;
};

export async function adminFetchRevenue(
  q: RevenueQuery,
): Promise<RevenueResponse> {
  const { data } = await api.get<RevenueResponse>("/admin/revenue", {
    params: q,
  });
  return data;
}
